import { useMemo, useState } from "react"
import toast from "react-hot-toast"
import { ChevronDown, Key, Plus, RefreshCw, Search, Trash2, X } from "lucide-react"

import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"

interface SecretMeta {
  id: string
  project: string
  environment: string
  key: string
  description: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

function SecretsScreen() {
  const [searchQuery, setSearchQuery] = useState("")
  const [projectFilter, setProjectFilter] = useState("all")
  const [envFilter, setEnvFilter] = useState("all")
  const [showAddForm, setShowAddForm] = useState(false)

  const { data: secrets, isLoading, refetch } = trpc.secrets.list.useQuery()

  const deleteMutation = trpc.secrets.delete.useMutation({
    onSuccess: () => {
      toast.success("Secret deleted")
      void refetch()
    },
    onError: (e) => toast.error(e.message || "Failed to delete"),
  })

  const setMutation = trpc.secrets.set.useMutation({
    onSuccess: () => {
      toast.success("Secret saved")
      setShowAddForm(false)
      void refetch()
    },
    onError: (e) => toast.error(e.message || "Failed to save"),
  })

  // Get unique projects and environments
  const projects = useMemo(() => {
    if (!secrets) return []
    return [...new Set((secrets as unknown as SecretMeta[]).map((s) => s.project))].sort()
  }, [secrets])

  const environments = useMemo(() => {
    if (!secrets) return []
    return [...new Set((secrets as unknown as SecretMeta[]).map((s) => s.environment))].sort()
  }, [secrets])

  // Filter secrets
  const filtered = useMemo(() => {
    if (!secrets) return []
    return (secrets as unknown as SecretMeta[]).filter((s) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        if (
          !s.key.toLowerCase().includes(q) &&
          !s.project.toLowerCase().includes(q) &&
          !(s.description || "").toLowerCase().includes(q)
        ) {
          return false
        }
      }
      if (projectFilter !== "all" && s.project !== projectFilter) return false
      if (envFilter !== "all" && s.environment !== envFilter) return false
      return true
    })
  }, [secrets, searchQuery, projectFilter, envFilter])

  // Group by project
  const grouped = useMemo(() => {
    const map = new Map<string, SecretMeta[]>()
    for (const s of filtered) {
      if (!map.has(s.project)) map.set(s.project, [])
      map.get(s.project)!.push(s)
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [filtered])

  const handleDelete = (secret: SecretMeta) => {
    if (!confirm(`Delete ${secret.project}/${secret.environment}/${secret.key}?`)) return
    deleteMutation.mutate({ project: secret.project, environment: secret.environment, key: secret.key })
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Key className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold text-foreground">Secrets Manager</h1>
          <span className="text-sm text-muted-foreground">
            {filtered.length} secret{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={() => setShowAddForm(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Secret
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="p-4 space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by key, project, description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-secondary border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>

        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground font-medium">Filters</span>

          {/* Environment chips */}
          <div className="flex items-center gap-1">
            {environments.map((env) => (
              <button
                key={env}
                onClick={() => setEnvFilter(envFilter === env ? "all" : env)}
                className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
                  envFilter === env
                    ? env === "prod"
                      ? "bg-green-500/20 text-green-400 ring-1 ring-green-500/40"
                      : env === "dev"
                        ? "bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40"
                        : "bg-gray-500/20 text-gray-400 ring-1 ring-gray-500/40"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {env}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Project chips */}
          <div className="flex items-center gap-1 flex-wrap">
            {projects.map((p) => (
              <button
                key={p}
                onClick={() => setProjectFilter(projectFilter === p ? "all" : p)}
                className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
                  projectFilter === p
                    ? "bg-primary/20 text-primary ring-1 ring-primary/40"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Add Secret Form */}
      {showAddForm && (
        <AddSecretForm
          onSave={(data) => setMutation.mutate(data)}
          onCancel={() => setShowAddForm(false)}
          isSaving={setMutation.isLoading}
          projects={projects}
        />
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3" />
            Loading secrets...
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No secrets found</div>
        ) : (
          <div className="space-y-4">
            {grouped.map(([project, projectSecrets]) => (
              <ProjectGroup key={project} project={project} secrets={projectSecrets} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ProjectGroup({
  project,
  secrets,
  onDelete,
}: {
  project: string
  secrets: SecretMeta[]
  onDelete: (s: SecretMeta) => void
}) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between p-3 bg-secondary hover:bg-secondary/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "-rotate-90" : ""}`}
          />
          <span className="font-medium text-foreground">{project}</span>
          <span className="text-xs text-muted-foreground">
            {secrets.length} secret{secrets.length !== 1 ? "s" : ""}
          </span>
        </div>
      </button>

      {!collapsed && (
        <div className="divide-y divide-border">
          {secrets.map((secret) => {
            return (
              <div key={secret.id} className="p-3 hover:bg-secondary/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Key className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm text-foreground">{secret.key}</code>
                        <EnvBadge env={secret.environment} />
                      </div>
                      {secret.description && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{secret.description}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => onDelete(secret)}
                      className="p-1.5 rounded hover:bg-border transition-colors text-red-400 hover:text-red-300"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-1 ml-7 text-[10px] text-muted-foreground">
                  Updated{" "}
                  {new Date(secret.updatedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function EnvBadge({ env }: { env: string }) {
  const cls =
    env === "prod"
      ? "bg-green-500/20 text-green-400"
      : env === "dev"
        ? "bg-blue-500/20 text-blue-400"
        : "bg-gray-500/20 text-gray-400"

  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${cls}`}>{env}</span>
}

function AddSecretForm({
  onSave,
  onCancel,
  isSaving,
  projects,
}: {
  onSave: (data: { project: string; environment: string; key: string; value: string; description?: string }) => void
  onCancel: () => void
  isSaving: boolean
  projects: string[]
}) {
  const [project, setProject] = useState("")
  const [environment, setEnvironment] = useState("prod")
  const [key, setKey] = useState("")
  const [value, setValue] = useState("")
  const [description, setDescription] = useState("")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!project || !key || !value) {
      toast.error("Project, key, and value are required")
      return
    }
    onSave({ project, environment, key, value, description: description || undefined })
  }

  return (
    <div className="mx-4 mb-4 border border-border rounded-lg p-4 bg-secondary">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-foreground">Add Secret</h3>
        <button onClick={onCancel} className="p-1 hover:bg-border rounded">
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Project</label>
          <Input
            type="text"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            placeholder="e.g. pebble"
            list="project-list"
            className="bg-background"
          />
          <datalist id="project-list">
            {projects.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Environment</label>
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground"
          >
            <option value="prod">prod</option>
            <option value="dev">dev</option>
            <option value="local">local</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Key</label>
          <Input
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="e.g. api_key"
            className="bg-background"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Value</label>
          <Input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Secret value"
            className="bg-background"
          />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground mb-1 block">Description (optional)</label>
          <Input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this secret for?"
            className="bg-background"
          />
        </div>
        <div className="col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={isSaving}>
            Save Secret
          </Button>
        </div>
      </form>
    </div>
  )
}

export default SecretsScreen
